import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Divider,
  Anchor,
  Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { useAuth } from '../../context/AuthContext';
import { Logo } from '../../components/common';

interface FormValues {
  inviteCode: string;
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
  dateOfBirth: Date | null;
}

function formatDob(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length > 4) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
}

function parseDob(formatted: string): Date | null {
  const digits = formatted.replace(/\D/g, '');
  if (digits.length !== 8) return null;
  const month = parseInt(digits.slice(0, 2), 10);
  const day = parseInt(digits.slice(2, 4), 10);
  const year = parseInt(digits.slice(4, 8), 10);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [dobText, setDobText] = useState('');

  const form = useForm<FormValues>({
    initialValues: {
      inviteCode: '',
      email: '',
      username: '',
      password: '',
      confirmPassword: '',
      dateOfBirth: null,
    },
    validate: {
      inviteCode: (value) => (value.length > 0 ? null : 'Invite code is required'),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      username: (value) =>
        value.length >= 3 && value.length <= 20
          ? null
          : 'Username must be between 3 and 20 characters',
      password: (value) => {
        if (value.length < 8) return 'Password must be at least 8 characters';
        if (!/[A-Z]/.test(value)) return 'Password must contain an uppercase letter';
        if (!/[a-z]/.test(value)) return 'Password must contain a lowercase letter';
        if (!/\d/.test(value)) return 'Password must contain a number';
        if (!/[^A-Za-z0-9]/.test(value)) return 'Password must contain a special character';
        return null;
      },
      confirmPassword: (value, values) =>
        value === values.password ? null : 'Passwords do not match',
      dateOfBirth: (value) => {
        if (!value) return 'Date of birth is required';
        const age = Math.floor(
          (Date.now() - value.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
        );
        return age >= 13 ? null : 'You must be at least 13 years old';
      },
    },
  });

  const registerMutation = useMutation({
    mutationFn: (values: FormValues) => register({
      email: values.email,
      username: values.username,
      password: values.password,
      dateOfBirth: values.dateOfBirth!.toISOString().split('T')[0],
      inviteCode: values.inviteCode,
    }),
    onSuccess: (_, variables) =>
      navigate(`/verify-email-sent?email=${encodeURIComponent(variables.email)}`, { replace: true }),
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to register. Please try again.'),
  });

  const handleSubmit = (values: FormValues) => {
    setError('');
    registerMutation.mutate(values);
  };

  return (
    <Container size={420} my={40}>
      <Stack align="center" mb="lg">
        <Logo size="lg" />
        <Title order={2} ta="center">
          Join CartCritic
        </Title>
        <Text c="dimmed" size="sm" ta="center">
          Already have an account?{' '}
          <Anchor component={Link} to="/login" size="sm">
            Log in
          </Anchor>
        </Text>
      </Stack>

      <Paper withBorder shadow="md" p={30} radius="md">
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
            {error}
          </Alert>
        )}

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              label="Invite Code"
              placeholder="Enter your invite code"
              required
              {...form.getInputProps('inviteCode')}
            />

            <TextInput
              label="Email"
              placeholder="you@example.com"
              required
              {...form.getInputProps('email')}
            />

            <TextInput
              label="Username"
              placeholder="Your username"
              required
              {...form.getInputProps('username')}
            />

            <TextInput
              label="Date of Birth"
              placeholder="MM/DD/YYYY"
              required
              value={dobText}
              onChange={(e) => {
                const formatted = formatDob(e.currentTarget.value);
                setDobText(formatted);
                form.setFieldValue('dateOfBirth', parseDob(formatted));
              }}
              error={form.errors.dateOfBirth}
              maxLength={10}
              inputMode="numeric"
            />

            <PasswordInput
              label="Password"
              placeholder="Create a password"
              required
              {...form.getInputProps('password')}
            />

            <PasswordInput
              label="Confirm Password"
              placeholder="Confirm your password"
              required
              {...form.getInputProps('confirmPassword')}
            />

            <Text size="xs" c="dimmed">
              By signing up, you agree to our{' '}
              <Anchor component={Link} to="/terms" size="xs">Terms of Service</Anchor>
              {' '}and{' '}
              <Anchor component={Link} to="/privacy" size="xs">Privacy Policy</Anchor>.
            </Text>

            <Button type="submit" fullWidth loading={registerMutation.isPending}>
              Create account
            </Button>
          </Stack>
        </form>

        <Divider label="Or continue with" labelPosition="center" my="lg" />

        <Button variant="default" fullWidth disabled>
          Google (Coming soon)
        </Button>
      </Paper>
    </Container>
  );
}
